import React from 'react';
import {
  NumberInput,
  SimpleForm,
  TextInput,
  minValue,
  maxLength,
  required,
  useResourceContext,
  useTranslate,
} from 'react-admin';
import { Grid, useTheme, useMediaQuery } from '@mui/material';
import { MyToolbar } from '@/components/forms';
import { useIsEditMode } from '@/hooks/useIsEditMode';
import {
  MAX_COST_CENTER_DESCRIPTION_LENGTH,
  MAX_COST_CENTER_NAME_LENGTH,
} from '../constants';

const i18nBase = 'resources.credit-consumption/cost-center';

const useValidationMessages = () => {
  const translate = useTranslate();
  const fieldLabel = (key: string): string =>
    translate(`${i18nBase}.fields.${key}`);
  return {
    requiredMsg: (key: string): string =>
      translate(`${i18nBase}.validation.required`, {
        field: fieldLabel(key),
      }),
    maxLengthMsg: (key: string, max: number): string =>
      translate(`${i18nBase}.validation.maxLength`, {
        field: fieldLabel(key),
        max,
      }),
    minValueMsg: (key: string, min: number): string =>
      translate(`${i18nBase}.validation.minValue`, {
        field: fieldLabel(key),
        min,
      }),
    positiveMsg: (key: string): string =>
      translate(`${i18nBase}.validation.positive`, {
        field: fieldLabel(key),
      }),
  };
};

export const CostCenterForm: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const resource = useResourceContext();
  const isEdit = useIsEditMode();
  const { requiredMsg, maxLengthMsg, minValueMsg } = useValidationMessages();

  return (
    <SimpleForm
      toolbar={<MyToolbar resource={resource} />}
      mode="all"
      reValidateMode="onChange"
      criteriaMode="all"
      shouldFocusError
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pb: isMobile ? 10 : 0,
      }}
    >
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        <Grid item xs={12} md={6}>
          <TextInput
            source="name"
            label={translate(`${i18nBase}.fields.name`)}
            validate={[
              required(requiredMsg('name')),
              maxLength(
                MAX_COST_CENTER_NAME_LENGTH,
                maxLengthMsg('name', MAX_COST_CENTER_NAME_LENGTH)
              ),
            ]}
            fullWidth
            autoFocus={!isEdit}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput
            source="description"
            label={translate(`${i18nBase}.fields.description`)}
            validate={[
              maxLength(
                MAX_COST_CENTER_DESCRIPTION_LENGTH,
                maxLengthMsg('description', MAX_COST_CENTER_DESCRIPTION_LENGTH)
              ),
            ]}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <NumberInput
            source="unpaidCreditLimit"
            label={translate(`${i18nBase}.fields.unpaidCreditLimit`)}
            validate={[minValue(0, minValueMsg('unpaidCreditLimit', 0))]}
            min={0}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </SimpleForm>
  );
};

export default CostCenterForm;
